import type { AuthInterface } from '@/client/hooks/use-auth';
import localForage from 'localforage';

// Shared with the co-hosted /usdt app. Only an explicit sign-in clears it.
const SIGNED_OUT_KEY = 'dpin-site-switch-signed-out';
const AUTH_KEYS = ['auth-token', 'dpin-auth-token-v1', 'dpin:auth-token'];
const WALLET_PREFIXES = ['wagmi.', 'dpin:wagmi.'];
const QUERY_DATABASES = ['query-0.7', 'dpin-public-cache-v1'];

export const isSiteSwitchSignedOut = () => {
  try {
    return window.sessionStorage.getItem(SIGNED_OUT_KEY) === '1';
  } catch {
    return false;
  }
};

export const allowExplicitSiteLogin = () => {
  try {
    window.sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    // Explicit wallet actions still work without session storage.
  }
};

let switching: Promise<void> | undefined;
export const isSiteSwitchInProgress = () => !!switching;

export const switchSite = (href: string, logout: AuthInterface['logout']): Promise<void> => {
  if (switching) return switching;
  const request = (async () => {
    // Invalidate live SIWE continuations before clearing persisted credentials.
    await logout({ silent: true });
    window.sessionStorage.setItem(SIGNED_OUT_KEY, '1');
    const storage = window.localStorage;
    const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
    for (const key of keys) {
      if (
        key &&
        (AUTH_KEYS.includes(key) ||
          (WALLET_PREFIXES.some(prefix => key.startsWith(prefix)) && !key.endsWith('.disconnected')))
      ) {
        storage.removeItem(key);
      }
    }
    // Remove only the query snapshot, retaining preferences and transaction journals.
    await Promise.all(
      QUERY_DATABASES.map(name => localForage.createInstance({ name }).removeItem('REACT_QUERY_OFFLINE_CACHE'))
    );
    window.location.assign(href);
  })();
  switching = request;
  // Keep the lock while the old document waits for the destination to load.
  // A failed cleanup can be retried without reloading this page.
  void request.catch(() => {
    switching = undefined;
  });
  return request;
};
